import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ProductCsvImport from './ProductCsvImport';
import PriceTagsPrint from './PriceTagsPrint';

function formatNumber(n) {
  return Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function groupFolderHue(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i += 1) h = name.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h) % 360;
}

function normalizePosComments(value) {
  const values = Array.isArray(value) ? value : String(value || '').split(/\r?\n/);
  return [...new Set(values.map((entry) => String(entry || '').trim()).filter(Boolean))];
}

/** Full body for PUT /api/products/:id (matches admin product form / Joi). */
function buildPayloadFromProduct(p, overrides = {}) {
  const o = { ...p, ...overrides };
  const num = (v) => {
    if (v === '' || v == null) return undefined;
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  };
  return {
    name: o.name,
    category: o.category,
    price: Number(o.price) || 0,
    image: o.image || '',
    description: o.description || '',
    inStock: o.inStock !== false,
    sku: o.sku || undefined,
    barcode: o.barcode || undefined,
    cost: num(o.cost),
    markupPercent: num(o.markupPercent),
    quantityOnHand: num(o.quantityOnHand),
    priceIncludesTax: Boolean(o.priceIncludesTax),
    isServiceItem: Boolean(o.isServiceItem),
    unitOfMeasurement: o.unitOfMeasurement || 'pc',
    defaultQuantity: num(o.defaultQuantity),
    ageRestriction: num(o.ageRestriction),
    internalNote: o.internalNote || undefined,
    posComments: normalizePosComments(o.posComments ?? o.posCommentsText),
    ...(o.storeId ? { storeId: String(o.storeId._id || o.storeId) } : {}),
    ...(o.taxRateId ? { taxRateId: String(o.taxRateId._id || o.taxRateId) } : {})
  };
}

export default function ProductsManager({ authConfig, backendUrl, onUnauthorized, stores = [], selectedStoreId = '', onStoreChange }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [showPriceTags, setShowPriceTags] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formTab, setFormTab] = useState('details');
  const [imageFetching, setImageFetching] = useState(false);
  const [imageFetchError, setImageFetchError] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [removeBackgroundOnUpload, setRemoveBackgroundOnUpload] = useState(true);
  const [form, setForm] = useState({
    name: '', price: '', category: '', image: '', description: '', inStock: true,
    sku: '', barcode: '', cost: '', markupPercent: '', quantityOnHand: '',
    priceIncludesTax: false, isServiceItem: false,
    unitOfMeasurement: 'pc', defaultQuantity: '', ageRestriction: '', internalNote: '',
    posCommentsText: '', storeId: ''
  });

  /** Category names with no products yet (empty groups). */
  const [extraCategories, setExtraCategories] = useState([]);
  /** Right-click menu: target 'all' = All row, else category name. */
  const [groupContextMenu, setGroupContextMenu] = useState(null);
  const groupMenuRef = useRef(null);
  /** Group name modal: new | edit */
  const [groupModal, setGroupModal] = useState(null);
  const [groupModalName, setGroupModalName] = useState('');

  const categories = useMemo(() => {
    const fromProducts = new Set(products.map((p) => p.category).filter(Boolean));
    const defaults = [
      'Computer Parts',
      'PisoWiFi/PisoNet',
      'CCTV Products',
      'Fiber Products',
      'Solar Products'
    ];
    const merged = [...defaults, ...fromProducts, ...extraCategories];
    return merged.filter((c, i, a) => a.indexOf(c) === i);
  }, [products, extraCategories]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = selectedStoreId ? { storeId: selectedStoreId } : {};
      const { data } = await axios.get(`${backendUrl}/api/products`, { params, ...authConfig });
      const list = Array.isArray(data) ? data : [];
      setProducts(list);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError('Failed to load products.');
    } finally {
      setLoading(false);
    }
  }, [backendUrl, authConfig, selectedStoreId, onUnauthorized]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    if (!groupContextMenu) return;
    const onDown = (e) => {
      if (groupMenuRef.current && !groupMenuRef.current.contains(e.target)) setGroupContextMenu(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setGroupContextMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [groupContextMenu]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sku || '').toLowerCase().includes(q) ||
        (p.barcode || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      );
    }
    if (selectedGroup) list = list.filter((p) => (p.category || '') === selectedGroup);
    return list;
  }, [products, search, selectedGroup]);

  const handleRefresh = () => loadProducts();

  const closeGroupMenu = () => setGroupContextMenu(null);

  const openNewGroupModal = useCallback(() => {
    closeGroupMenu();
    setGroupModal({ mode: 'new' });
    setGroupModalName('');
    setError('');
  }, []);

  const openEditGroupModal = useCallback((categoryName) => {
    if (!categoryName) return;
    closeGroupMenu();
    setGroupModal({ mode: 'edit', original: categoryName });
    setGroupModalName(categoryName);
    setError('');
  }, []);

  const submitGroupModal = async (e) => {
    e.preventDefault();
    const name = groupModalName.trim();
    if (!name) return;
    if (!groupModal) return;
    setError('');
    if (groupModal.mode === 'new') {
      if (categories.includes(name)) {
        setError('A group with that name already exists.');
        return;
      }
      setExtraCategories((ex) => [...new Set([...ex, name])]);
      setSelectedGroup(name);
      setGroupModal(null);
      setGroupModalName('');
      return;
    }
    const { original } = groupModal;
    if (name === original) {
      setGroupModal(null);
      setGroupModalName('');
      return;
    }
    try {
      const affected = products.filter((p) => (p.category || '') === original);
      for (const p of affected) {
        await axios.put(
          `${backendUrl}/api/products/${p._id}`,
          buildPayloadFromProduct(p, { category: name }),
          authConfig
        );
      }
      setExtraCategories((ex) => [...new Set(ex.map((c) => (c === original ? name : c)))]);
      if (selectedGroup === original) setSelectedGroup(name);
      setGroupModal(null);
      setGroupModalName('');
      await loadProducts();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to rename group.');
    }
  };

  const deleteGroupByName = async (categoryName) => {
    if (!categoryName) return;
    closeGroupMenu();
    const inGroup = products.filter((p) => (p.category || '') === categoryName);
    const msg =
      inGroup.length > 0
        ? `Remove group "${categoryName}"? ${inGroup.length} product(s) will be moved to Uncategorized.`
        : `Remove empty group "${categoryName}"?`;
    if (!window.confirm(msg)) return;
    setError('');
    try {
      for (const p of inGroup) {
        await axios.put(
          `${backendUrl}/api/products/${p._id}`,
          buildPayloadFromProduct(p, { category: 'Uncategorized' }),
          authConfig
        );
      }
      setExtraCategories((ex) => ex.filter((c) => c !== categoryName));
      if (selectedGroup === categoryName) setSelectedGroup('');
      await loadProducts();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to remove group.');
    }
  };

  const handlePrint = () => window.print();
  const handleSavePdf = () => window.print();
  const baseForm = () => ({
    name: '', price: '', category: categories[0] || '', image: '', description: '', inStock: true,
    sku: '', barcode: '', cost: '', markupPercent: '', quantityOnHand: '', priceIncludesTax: false, isServiceItem: false,
    unitOfMeasurement: 'pc', defaultQuantity: '', ageRestriction: '', internalNote: '',
    posCommentsText: '', storeId: selectedStoreId || (stores[0]?._id || stores[0]?.id || '')
  });
  const handleNewProduct = () => { setEditingId(null); setForm(baseForm()); setFormTab('details'); setImageFetchError(''); setShowForm(true); };
  const handleEditProduct = (p) => {
    setEditingId(p._id);
    setForm({
      name: p.name || '', price: p.price ?? '', category: p.category || '', image: p.image || '', description: p.description || '', inStock: p.inStock !== false,
      sku: p.sku || '', barcode: p.barcode || '', cost: p.cost ?? '', markupPercent: p.markupPercent ?? '', quantityOnHand: p.quantityOnHand ?? '',
      priceIncludesTax: Boolean(p.priceIncludesTax), isServiceItem: Boolean(p.isServiceItem),
      unitOfMeasurement: p.unitOfMeasurement || 'pc', defaultQuantity: p.defaultQuantity ?? '', ageRestriction: p.ageRestriction ?? '', internalNote: p.internalNote || '',
      posCommentsText: normalizePosComments(p.posComments).join('\n'), storeId: p.storeId ? String(p.storeId._id || p.storeId) : ''
    });
    setFormTab('details');
    setImageFetchError('');
    setShowForm(true);
  };
  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${backendUrl}/api/products/${id}`, authConfig);
      await loadProducts();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError('Failed to delete product.');
    }
  };
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const resolvedStore =
        (selectedStoreId || form.storeId || '').trim() || undefined;
      const payload = {
        name: form.name, category: form.category, price: Number(form.price) || 0, image: form.image || '', description: form.description || '', inStock: form.inStock,
        sku: form.sku || undefined, barcode: form.barcode || undefined, cost: form.cost !== '' ? Number(form.cost) : undefined,
        markupPercent: form.markupPercent !== '' ? Number(form.markupPercent) : undefined, quantityOnHand: form.quantityOnHand !== '' ? Number(form.quantityOnHand) : undefined,
        priceIncludesTax: form.priceIncludesTax, isServiceItem: form.isServiceItem,
        unitOfMeasurement: form.unitOfMeasurement || 'pc',
        defaultQuantity: form.defaultQuantity !== '' ? Number(form.defaultQuantity) : undefined,
        ageRestriction: form.ageRestriction !== '' ? Number(form.ageRestriction) : undefined,
        internalNote: form.internalNote || undefined,
        posComments: normalizePosComments(form.posCommentsText),
        ...(resolvedStore ? { storeId: resolvedStore } : {})
      };
      if (editingId) await axios.put(`${backendUrl}/api/products/${editingId}`, payload, authConfig);
      else await axios.post(`${backendUrl}/api/products`, payload, authConfig);
      setShowForm(false);
      await loadProducts();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setError(err?.response?.data?.error || 'Failed to save product.');
    }
  };

  const generateBarcode = () => {
    const r = () => Math.floor(Math.random() * 10);
    const digits = Array.from({ length: 13 }, r).join('');
    setForm((f) => ({ ...f, barcode: digits }));
  };

  const pickImageUrl = (p) => p?.image_front_url || p?.image_small_url || p?.image_url;

  const handleUploadImage = useCallback(async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      setImageUploadError('Please choose an image file (JPG, PNG, etc.).');
      return;
    }
    setImageUploadError('');
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      if (removeBackgroundOnUpload) formData.append('removeBackground', 'true');
      const { data } = await axios.post(`${backendUrl}/api/upload`, formData, authConfig);
      if (data?.imagePath) {
        setForm((f) => ({ ...f, image: data.imagePath }));
      }
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.(err);
      else setImageUploadError(err?.response?.data?.error || 'Upload failed.');
    } finally {
      setImageUploading(false);
      e.target.value = '';
    }
  }, [backendUrl, authConfig, onUnauthorized, removeBackgroundOnUpload]);

  const handleFindImage = async () => {
    const barcode = (form.barcode || '').trim().replace(/\D/g, '');
    const name = (form.name || '').trim();
    if (!barcode && !name) {
      setImageFetchError('Enter a product name or barcode to find an image.');
      return;
    }
    setImageFetching(true);
    setImageFetchError('');
    const ua = { headers: { 'User-Agent': 'iConnectStore/1.0 (Product catalog)' } };
    try {
      if (barcode) {
        const res = await fetch(`https://world.openfoodfacts.org/api/v2/product/${barcode}.json`, ua);
        const data = await res.json();
        const url = pickImageUrl(data?.product);
        if (url) {
          setForm((f) => ({ ...f, image: url }));
          setImageFetching(false);
          return;
        }
      }
      if (name) {
        const res = await fetch(
          `https://world.openfoodfacts.org/cgi/search.pl?search_terms=${encodeURIComponent(name)}&search_simple=1&action=process&json=1&page_size=8`,
          ua
        );
        const data = await res.json();
        const products = data?.products || [];
        const hit = products.find((p) => pickImageUrl(p));
        const url = pickImageUrl(hit);
        if (url) {
          setForm((f) => ({ ...f, image: url }));
          setImageFetching(false);
          return;
        }
      }
      setImageFetchError('No image found. Open Food Facts has mostly food/drink products.');
    } catch (err) {
      setImageFetchError('Could not fetch image. Check connection.');
    } finally {
      setImageFetching(false);
    }
  };

  const handleImport = async (_rows, config, onAuth, onProgress, csvText, duplicateMode) => {
    return runCsvImportStream(config, onAuth, onProgress, csvText, duplicateMode);
  };

  const handleImportFromBackend = async (config, onAuth, onProgress, duplicateMode) => {
    return runCsvImportStream(config, onAuth, onProgress, undefined, duplicateMode);
  };

  const runCsvImportStream = async (config, onAuth, onProgress, csvText, duplicateMode = 'update') => {
    const res = await fetch(`${backendUrl}/api/products/import-csv-stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(config?.headers || {}) },
      body: JSON.stringify({ storeId: selectedStoreId || undefined, csvText: csvText || undefined, duplicateMode })
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `HTTP ${res.status}`);
    }
    if (res.status === 401 && onAuth) { onAuth({ response: { status: 401 } }); throw new Error('Unauthorized'); }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = { imported: 0, total: 0, errors: [] };
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const data = JSON.parse(line);
          if (data.done) result = data;
          else if (data.total !== undefined && onProgress) onProgress(data);
        } catch (_) {}
      }
    }
    if (buffer.trim()) {
      try {
        const data = JSON.parse(buffer);
        if (data.done) result = data;
      } catch (_) {}
    }
    await loadProducts();
    return { ...result, message: result.errors?.length ? result.errors.join('; ') : `Imported ${result.imported} of ${result.total} products.` };
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
        <h2 className="text-lg font-semibold">Management • Products</h2>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={handleRefresh} className="px-2 py-1 rounded text-sm border hover:bg-gray-100">Refresh</button>
          <button type="button" onClick={openNewGroupModal} className="px-2 py-1 rounded text-sm border hover:bg-gray-100">New group</button>
          <button type="button" onClick={() => openEditGroupModal(selectedGroup)} disabled={!selectedGroup} className="px-2 py-1 rounded text-sm border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">Edit group</button>
          <button type="button" onClick={() => deleteGroupByName(selectedGroup)} disabled={!selectedGroup} className="px-2 py-1 rounded text-sm border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">Delete group</button>
          <button type="button" onClick={handleNewProduct} className="px-2 py-1 rounded text-sm bg-indigo-600 text-white hover:bg-indigo-500">New product</button>
          <button type="button" onClick={handlePrint} className="px-2 py-1 rounded text-sm border">Print</button>
          <button type="button" onClick={handleSavePdf} className="px-2 py-1 rounded text-sm border">Save as PDF</button>
          <button type="button" onClick={() => setShowPriceTags(true)} className="px-2 py-1 rounded text-sm border">Price tags</button>
          <button type="button" onClick={() => setShowImport(!showImport)} className="px-2 py-1 rounded text-sm border">Import</button>
          <button type="button" className="px-2 py-1 rounded text-sm border">Export</button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Left: product groups */}
        <div className="w-56 border-r overflow-y-auto p-2 shrink-0">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Product groups</p>
          <button
            type="button"
            onClick={() => setSelectedGroup('')}
            onContextMenu={(e) => {
              e.preventDefault();
              setGroupContextMenu({ x: e.clientX, y: e.clientY, target: 'all' });
            }}
            className={`w-full text-left px-2 py-1.5 rounded text-sm mb-1 flex items-center gap-2 ${!selectedGroup ? 'bg-indigo-100 text-indigo-800' : 'hover:bg-gray-100'}`}
          >
            <span className="w-3.5 h-3.5 rounded-sm bg-indigo-500 shrink-0 shadow-sm" aria-hidden />
            <span>All</span>
          </button>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setSelectedGroup(c)}
              onContextMenu={(e) => {
                e.preventDefault();
                setGroupContextMenu({ x: e.clientX, y: e.clientY, target: c });
              }}
              className={`w-full text-left px-2 py-1.5 rounded text-sm mb-1 flex items-center gap-2 min-w-0 ${selectedGroup === c ? 'bg-indigo-100 text-indigo-800' : 'hover:bg-gray-100'}`}
            >
              <span
                className="w-3.5 h-3.5 rounded-sm shrink-0 shadow-sm"
                style={{ backgroundColor: `hsl(${groupFolderHue(c)} 55% 42%)` }}
                aria-hidden
              />
              <span className="truncate">{c}</span>
            </button>
          ))}
        </div>

        {/* Center: search + product table */}
        <div className="flex-1 flex flex-col min-w-0">
          {showImport && (
            <div className="border-b p-3 bg-gray-50">
              <ProductCsvImport
                onImport={handleImport}
                onImportFromBackend={handleImportFromBackend}
                authConfig={authConfig}
                onUnauthorized={onUnauthorized}
              />
            </div>
          )}
          <div className="flex items-center gap-2 p-2 border-b">
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Product name" className="flex-1 max-w-xs border rounded px-3 py-1.5 text-sm" />
            {stores.length > 0 && onStoreChange && (
              <select value={selectedStoreId} onChange={(e) => onStoreChange(e.target.value)} className="border rounded px-2 py-1.5 text-sm">
                <option value="">All stores</option>
                {stores.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
              </select>
            )}
            <span className="text-sm text-gray-500 ml-auto">Products count: {filteredProducts.length}</span>
          </div>
          {error && <p className="text-red-600 text-sm px-4 py-1">{error}</p>}
          {loading ? (
            <p className="p-4 text-gray-500">Loading...</p>
          ) : (
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Code</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Group</th>
                    <th className="text-left p-2">Barcode</th>
                    <th className="text-right p-2">Cost</th>
                    <th className="text-right p-2">Sale price</th>
                    <th className="text-left p-2">Active</th>
                    <th className="text-left p-2">Unit</th>
                    <th className="text-left p-2">Created</th>
                    <th className="text-left p-2">Updated</th>
                    <th className="w-20" />
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => (
                    <tr key={p._id} className={`border-b hover:bg-gray-50 ${selectedProductId === p._id ? 'bg-indigo-50' : ''}`} onClick={() => setSelectedProductId(p._id)}>
                      <td className="p-2">{p.sku || p._id?.slice(-6)}</td>
                      <td className="p-2 truncate max-w-[200px]" title={p.name}>{p.name}</td>
                      <td className="p-2">{p.category || '—'}</td>
                      <td className="p-2">{p.barcode || '—'}</td>
                      <td className="p-2 text-right">{formatNumber(p.cost)}</td>
                      <td className="p-2 text-right">{formatNumber(p.price)}</td>
                      <td className="p-2">{p.inStock !== false ? '✓' : ''}</td>
                      <td className="p-2">pc</td>
                      <td className="p-2 text-gray-500">{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                      <td className="p-2 text-gray-500">{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : '—'}</td>
                      <td className="p-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleEditProduct(p); }} className="text-indigo-600 text-xs mr-1">Edit</button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteProduct(p._id); }} className="text-red-600 text-xs">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredProducts.length === 0 && <p className="p-4 text-gray-500">No products found.</p>}
            </div>
          )}
        </div>
      </div>

      {groupContextMenu && (
        <div
          ref={groupMenuRef}
          role="menu"
          className="fixed z-[60] min-w-[220px] rounded-md border border-zinc-600 bg-zinc-800 py-1 shadow-xl text-sm text-zinc-100"
          style={{
            left: Math.max(8, Math.min(groupContextMenu.x, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 228)),
            top: Math.max(8, Math.min(groupContextMenu.y, (typeof window !== 'undefined' ? window.innerHeight : 800) - 200))
          }}
        >
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-3 py-2 hover:bg-zinc-700"
            onClick={() => openNewGroupModal()}
          >
            New group
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={groupContextMenu.target === 'all'}
            className="w-full text-left px-3 py-2 hover:bg-zinc-700 disabled:opacity-40 disabled:pointer-events-none"
            onClick={() => openEditGroupModal(groupContextMenu.target)}
          >
            Edit group
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={groupContextMenu.target === 'all'}
            className="w-full text-left px-3 py-2 hover:bg-zinc-700 disabled:opacity-40 disabled:pointer-events-none"
            onClick={() => deleteGroupByName(groupContextMenu.target)}
          >
            Delete group
          </button>
          <div className="my-1 border-t border-zinc-600" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="w-full text-left px-3 py-2 hover:bg-zinc-700 flex justify-between items-center gap-4"
            onClick={() => {
              closeGroupMenu();
              handleRefresh();
            }}
          >
            <span>Refresh</span>
            <span className="text-zinc-400 text-xs font-mono">F5</span>
          </button>
        </div>
      )}

      {groupModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4"
        >
          <form
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitGroupModal}
          >
            <h3 className="font-semibold text-lg mb-4">
              {groupModal.mode === 'new' ? 'New group' : `Edit group '${(groupModal.original || '').slice(0, 36)}${(groupModal.original || '').length > 36 ? '…' : ''}'`}
            </h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              autoFocus
              className="border w-full p-2 rounded text-sm mb-4"
              value={groupModalName}
              onChange={(e) => setGroupModalName(e.target.value)}
              placeholder="Group name"
              required
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 border rounded text-sm" onClick={() => { setGroupModal(null); setGroupModalName(''); }}>
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-500">
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={handleFormSubmit} className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-lg">
                {editingId ? `Edit product '${(form.name || '').slice(0, 40)}${form.name?.length > 40 ? '...' : ''}'` : 'New product'}
              </h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700 p-1" aria-label="Close">×</button>
            </div>
            <div className="flex gap-0 border-b">
              {['details', 'price', 'stock', 'comments', 'image'].map((tab) => (
                <button key={tab} type="button" onClick={() => setFormTab(tab)} className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${formTab === tab ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {tab === 'details' ? 'Details' : tab === 'price' ? 'Price & tax' : tab === 'stock' ? 'Stock control' : tab === 'comments' ? 'Comments' : 'Image & color'}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-4">
              {formTab === 'details' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input name="name" className="border w-full p-2 rounded text-sm" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input name="sku" placeholder="Product code / SKU" className="border w-full p-2 rounded text-sm" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                    <div className="flex gap-2">
                      <input name="barcode" className="border flex-1 p-2 rounded text-sm" value={form.barcode} onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))} />
                      <button type="button" onClick={() => setForm((f) => ({ ...f, barcode: '' }))} className="text-gray-400 hover:text-gray-600 px-2" title="Clear">×</button>
                    </div>
                    <button type="button" onClick={generateBarcode} className="text-indigo-600 text-sm mt-1 hover:underline">Generate barcode</button>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit of measurement</label>
                    <input name="unitOfMeasurement" placeholder="pc, kg, etc." className="border w-full p-2 rounded text-sm" value={form.unitOfMeasurement} onChange={(e) => setForm((f) => ({ ...f, unitOfMeasurement: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                    <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="border w-full p-2 rounded text-sm" required>
                      <option value="">Select group</option>
                      {categories.map((c) => <option key={c} value={c}>Products / {c}</option>)}
                    </select>
                  </div>
                  {stores.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Store</label>
                      {selectedStoreId ? (
                        <p className="text-sm text-gray-600 py-2">
                          {stores.find((s) => String(s._id) === String(selectedStoreId))?.name || 'Current store'}
                        </p>
                      ) : (
                        <select
                          value={form.storeId}
                          onChange={(e) => setForm((f) => ({ ...f, storeId: e.target.value }))}
                          className="border w-full p-2 rounded text-sm"
                          required
                        >
                          <option value="">Select store (Monkayo / Tagum, etc.)</option>
                          {stores.map((s) => (
                            <option key={s._id} value={s._id}>{s.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4 items-center">
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.inStock} onChange={(e) => setForm((f) => ({ ...f, inStock: e.target.checked }))} /> Active</label>
                    <div className="flex items-center gap-2">
                      <label className="text-sm">Default quantity</label>
                      <input type="number" min="0" className="border w-16 p-1 rounded text-sm" value={form.defaultQuantity} onChange={(e) => setForm((f) => ({ ...f, defaultQuantity: e.target.value }))} />
                    </div>
                    <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isServiceItem} onChange={(e) => setForm((f) => ({ ...f, isServiceItem: e.target.checked }))} /> Service (not using stock)</label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age restriction</label>
                    <div className="flex gap-1 items-center">
                      <input name="ageRestriction" type="number" min="0" className="border w-20 p-2 rounded text-sm" value={form.ageRestriction} onChange={(e) => setForm((f) => ({ ...f, ageRestriction: e.target.value }))} />
                      <span className="text-sm text-gray-500">year(s)</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea name="description" rows={4} className="border w-full p-2 rounded text-sm" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                  </div>
                </>
              )}
              {formTab === 'price' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sale price</label>
                    <input name="price" type="number" step="0.01" min="0" className="border w-full p-2 rounded text-sm" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                    <input name="cost" type="number" step="0.01" min="0" className="border w-full p-2 rounded text-sm" value={form.cost} onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Markup %</label>
                    <input name="markupPercent" type="number" step="0.01" min="0" className="border w-full p-2 rounded text-sm" value={form.markupPercent} onChange={(e) => setForm((f) => ({ ...f, markupPercent: e.target.value }))} />
                  </div>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.priceIncludesTax} onChange={(e) => setForm((f) => ({ ...f, priceIncludesTax: e.target.checked }))} /> Price includes tax</label>
                </>
              )}
              {formTab === 'stock' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity on hand</label>
                    <input name="quantityOnHand" type="number" min="0" className="border w-full p-2 rounded text-sm" value={form.quantityOnHand} onChange={(e) => setForm((f) => ({ ...f, quantityOnHand: e.target.value }))} />
                  </div>
                </>
              )}
              {formTab === 'comments' && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Internal note</label>
                    <textarea name="internalNote" rows={3} className="border w-full p-2 rounded text-sm" value={form.internalNote} onChange={(e) => setForm((f) => ({ ...f, internalNote: e.target.value }))} placeholder="Internal notes (not shown to customers)" />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-gray-700">Selectable POS comments / serial numbers</label>
                      <span className="text-xs text-gray-500">{normalizePosComments(form.posCommentsText).length} available</span>
                    </div>
                    <textarea
                      name="posComments"
                      rows={9}
                      className="border w-full rounded p-2 font-mono text-sm"
                      value={form.posCommentsText}
                      onChange={(e) => setForm((f) => ({ ...f, posCommentsText: e.target.value }))}
                      placeholder={'One entry per line\nMG6RL-58832103\nMG6RL-58832102'}
                    />
                  </div>
                </div>
              )}
              {formTab === 'image' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                  <div className="flex flex-wrap gap-2 items-center mb-2">
                    <label className="px-3 py-2 bg-indigo-600 text-white rounded text-sm cursor-pointer hover:bg-indigo-500 disabled:opacity-50">
                      {imageUploading ? 'Uploading…' : 'Upload image'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleUploadImage} disabled={imageUploading} />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={removeBackgroundOnUpload}
                        onChange={(e) => setRemoveBackgroundOnUpload(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      Remove background (product only)
                    </label>
                    <span className="text-xs text-gray-500">or paste URL / Find by barcode below</span>
                  </div>
                  <div className="flex gap-2">
                    <input name="image" type="text" className="border flex-1 p-2 rounded text-sm" value={form.image || ''} onChange={(e) => { setForm((f) => ({ ...f, image: e.target.value })); setImageFetchError(''); setImageUploadError(''); }} placeholder="Upload above, or paste URL / find by barcode" />
                    <button type="button" onClick={handleFindImage} disabled={imageFetching || (!form.barcode?.trim() && !form.name?.trim())} className="px-3 py-2 bg-gray-600 text-white rounded text-sm whitespace-nowrap disabled:opacity-50 hover:bg-gray-500">
                      {imageFetching ? '…' : 'Find image'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Upload from your computer (saved to server/R2), or find by product name/barcode (Open Food Facts). When “Remove background” is on, the server uses remove.bg so only the product is visible.</p>
                  {imageUploadError && <p className="text-sm text-amber-600 mt-1">{imageUploadError}</p>}
                  {imageFetchError && <p className="text-sm text-amber-600 mt-1">{imageFetchError}</p>}
                  {form.image?.trim() && (
                    <img
                      key={form.image}
                      src={form.image.startsWith('http') ? form.image : `${backendUrl.replace(/\/$/, '')}${form.image}`}
                      alt="Product"
                      className="mt-2 h-32 object-contain border rounded"
                      loading="eager"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded text-sm flex items-center gap-2">× Cancel</button>
              <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded text-sm flex items-center gap-2 hover:bg-green-500">✓ Save</button>
            </div>
          </form>
        </div>
      )}

      {showPriceTags && <PriceTagsPrint products={products} onClose={() => setShowPriceTags(false)} />}
    </div>
  );
}
